/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useLayoutEffect, useState} from 'react';
import {
  APPLIED_FILTER_BAR_CHIP_GAP,
  APPLIED_FILTER_BAR_CONTENT_GAP
} from './AppliedFilterBar.styles';
import {AppliedFilterBarItem} from './AppliedFilterBarTypes';

interface MeasurementRefs {
  chipRefs: React.MutableRefObject<Record<string, HTMLSpanElement | null>>;
  clearControlsRef: React.RefObject<HTMLDivElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  leftControlsRef: React.RefObject<HTMLDivElement>;
  moreControlRef: React.RefObject<HTMLDivElement>;
}

interface MeasuredItem {
  width: number;
}

export interface UseAppliedFilterBarMeasurementsArgs {
  items: AppliedFilterBarItem[];
  refs: MeasurementRefs;
}

export const APPLIED_FILTER_BAR_CHIP_AREA_HORIZONTAL_PADDING = 16;
export const APPLIED_FILTER_BAR_CHIP_FIT_BUFFER = 8;

export interface AppliedFilterBarVisibleCountArgs {
  clearWidth: number;
  containerWidth: number;
  itemWidths: number[];
  leftWidth: number;
  moreWidth: number;
}

/**
 * Calculates the number of chips that fit in the visible selected-filter row.
 */
export const getAppliedFilterBarVisibleCount = ({
  clearWidth,
  containerWidth,
  itemWidths,
  leftWidth,
  moreWidth
}: AppliedFilterBarVisibleCountArgs) => {
  const chipsWidth = itemWidths.reduce((total, width, index) => (
    total + width + (index ? APPLIED_FILTER_BAR_CHIP_GAP : 0)
  ), 0);
  const baseAvailable = containerWidth - leftWidth - clearWidth -
    (APPLIED_FILTER_BAR_CONTENT_GAP * 2) -
    APPLIED_FILTER_BAR_CHIP_AREA_HORIZONTAL_PADDING -
    APPLIED_FILTER_BAR_CHIP_FIT_BUFFER;

  if (chipsWidth <= baseAvailable) {
    return itemWidths.length;
  }

  const overflowAvailable = baseAvailable - moreWidth - APPLIED_FILTER_BAR_CONTENT_GAP;
  let occupiedWidth = 0;
  let nextVisibleCount = 0;
  let hasOverflowed = false;
  // the bar shows the leading chips (items.slice(0, count)), so counting stops
  // at the first chip that does not fit - a later, narrower chip that would
  // fit must not pull that wider one into the row, where it is clipped
  // without its close icon instead of moving to the more list ...
  itemWidths.forEach((width, index) => {
    const nextWidth = occupiedWidth + width + (index ? APPLIED_FILTER_BAR_CHIP_GAP : 0);
    hasOverflowed = hasOverflowed || nextWidth > overflowAvailable;
    if (!hasOverflowed) {
      occupiedWidth = nextWidth;
      nextVisibleCount += 1;
    }
  });
  return nextVisibleCount;
};

/**
 * Calculates how many applied filter chips fit in one filter-bar row.
 *
 * ```typescript
 * const visibleCount = useAppliedFilterBarMeasurements({items, refs});
 * ```
 */
export const useAppliedFilterBarMeasurements = ({
  items,
  refs: {
    chipRefs,
    clearControlsRef,
    containerRef,
    leftControlsRef,
    moreControlRef
  }
}: UseAppliedFilterBarMeasurementsArgs) => {
  const [visibleCount, setVisibleCount] = useState(items.length);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const measuredItems: MeasuredItem[] = items.map(item => ({
      width : chipRefs.current[item.id]?.getBoundingClientRect().width ?? 0
    }));
    const leftWidth = leftControlsRef.current?.getBoundingClientRect().width ?? 0;
    const clearWidth = clearControlsRef.current?.getBoundingClientRect().width ?? 0;
    const moreWidth = moreControlRef.current?.getBoundingClientRect().width ?? 0;
    setVisibleCount(getAppliedFilterBarVisibleCount({
      clearWidth,
      containerWidth : container.clientWidth,
      itemWidths     : measuredItems.map(({width}) => width),
      leftWidth,
      moreWidth
    }));
  }, [
    chipRefs,
    clearControlsRef,
    containerRef,
    items,
    leftControlsRef,
    moreControlRef
  ]);

  useLayoutEffect(() => {
    measure();
    const ResizeObserverClass = globalThis.window?.ResizeObserver;
    if (ResizeObserverClass && containerRef.current) {
      const observer = new ResizeObserverClass(measure);
      observer.observe(containerRef.current);
      return () => {
        observer.disconnect();
      };
    }
    return undefined;
  }, [containerRef, measure]);

  return visibleCount;
};

export default useAppliedFilterBarMeasurements;
